import React from "react";

// Component Imports
import FormGenerator from "@/pages/AlertManager/FormGenerator/FormGenerator";
import { DropdownInput } from "@/components/Inputs";

// Type Imports
import { IVenueApiKey } from "@pulse-ui/codegen/generated_ts/authn";
import { ISchemaConfig } from "@/pages/AlertManager/FormGenerator/FormGenerator.types";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { IOrgMetadata } from "@/context/context.types";

// MUI Imports
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { IPortfolioMetadata } from "@pulse-ui/codegen/generated_ts/org_metadata";

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  className: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function CustomDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#ebe93e",
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

interface IProps {
  open: boolean;
  handleClose: any;
  schemas: any;
  selectedSchemaName: string | undefined;
  setSelectedSchemaName: React.Dispatch<React.SetStateAction<any>>;
  apiKeys: IVenueApiKey[];
  portfolios: string[];
  org_metadata: IOrgMetadata;
  handleSubmit: (data: any) => void;
  preset?: any;
  setPreset: React.Dispatch<React.SetStateAction<any>>;
  slackChannels: string[];
  telegramChannels: string[];
  smsChannels: string[];
  assetOptions: string[];
  groupDestinationNames: string[];
  transformCheckType: (checkType: string) => string;
}

const getPortfolioDisplayName = (
  portfolio_id: string,
  metadata_portfolios: IPortfolioMetadata[]
) => {
  const portfolioMetadata = metadata_portfolios.find(
    (item) => item.portfolio_id === portfolio_id
  );
  return portfolioMetadata ? portfolioMetadata.display_name : portfolio_id;
};

function processSchema(
  schema: ISchemaConfig,
  apiKeys: IVenueApiKey[] | null,
  portfolios: string[],
  metadata_portfolios: IPortfolioMetadata[]
): ISchemaConfig {
  if (!schema.properties) return schema;

  // Deep-clone to avoid mutating RTK Query frozen cache objects
  const cloned: ISchemaConfig = JSON.parse(JSON.stringify(schema));
  const properties = cloned.properties!;
  let arrayObjectFieldKey: string | undefined;

  if (properties.event_type) delete properties.event_type;
  const required = cloned.required?.filter(
    (item: any) => item !== "event_type"
  );

  if (apiKeys && properties.account) {
    const keyIds = apiKeys.map((item: any) => ({
      const: item.account_id,
      title: item.key_id,
    }));
    properties.account["oneOf"] = keyIds;
    properties.account["default"] = "";
  }

  if (portfolios && properties.portfolio) {
    const keyIds = portfolios.map((item: any) => ({
      const: item,
      title: getPortfolioDisplayName(item, metadata_portfolios),
    }));
    properties.portfolio["oneOf"] = keyIds;
    properties.portfolio["default"] = "";
  }

  for (const key in properties) {
    if (
      properties[key].type === "array" &&
      properties[key].items?.type === "object"
    ) {
      arrayObjectFieldKey = key;
      break;
    }
  }

  if (arrayObjectFieldKey) {
    const { [arrayObjectFieldKey]: arrayObjectField, ...rest } = properties;
    return {
      ...cloned,
      properties: { ...rest, [arrayObjectFieldKey]: arrayObjectField },
      required,
    };
  }

  return { ...cloned, properties, required };
}

const FormModal = ({
  open,
  handleClose,
  schemas,
  selectedSchemaName,
  setSelectedSchemaName,
  apiKeys,
  portfolios,
  org_metadata,
  handleSubmit,
  preset,
  setPreset,
  slackChannels,
  telegramChannels,
  smsChannels,
  assetOptions,
  groupDestinationNames,
  transformCheckType,
}: IProps) => {
  const schemaOptions: ISelectOption[] = Object.keys(schemas).map(
    (option: string) => {
      return { label: transformCheckType(option), value: option };
    }
  );

  return (
    <div className="apikeys_modal">
      <CustomDialog
        onClose={() => {
          handleClose();
          setSelectedSchemaName(undefined);
          setPreset(undefined);
        }}
        open={open}
        className="apikeys_modal__dialog"
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            boxShadow: "0px 0px 10px #777",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          },
        }}
        fullScreen
      >
        <CustomDialogTitle
          className="apikeys_modal__dialog__title pulse-bg-color--background-800"
          onClose={() => {
            handleClose();
            setSelectedSchemaName(undefined);
            setPreset(undefined);
          }}
        >
          Start Alert
        </CustomDialogTitle>

        <DialogContent dividers className="pulse-bg-color--background-900">
          <div className="pulse-row pulse-mb-3">
            <div className="pulse-display-f pulse-align-center pulse-typography-body-1 pulse-color--background-100 pulse-mr-1">
              Check Type
            </div>

            <DropdownInput
              name="check_type"
              value={selectedSchemaName || null}
              label="Check Type"
              onChange={(e: any) => {
                setSelectedSchemaName(e.target.value);
              }}
              disabled={false}
              options={schemaOptions}
              formInput
              containerClassNames="pulse-col-xs-6 pulse-col-sm-6 pulse-col-md-6 pulse-col-lg-6 pulse-col-xl-6"
            />
          </div>
          <>
            {assetOptions &&
              schemas &&
              selectedSchemaName &&
              schemas[selectedSchemaName] && (
                <FormGenerator
                  assetOptions={assetOptions}
                  schema={processSchema(
                    schemas[selectedSchemaName],
                    apiKeys,
                    portfolios,
                    org_metadata.portfolios
                  )}
                  handleSubmit={handleSubmit}
                  preset={preset}
                  slackChannels={slackChannels}
                  telegramChannels={telegramChannels}
                  smsChannels={smsChannels}
                  groupDestinationNames={groupDestinationNames}
                  portfolios={portfolios || []}
                  org_metadata={org_metadata || {}}
                />
              )}
          </>
        </DialogContent>
      </CustomDialog>
    </div>
  );
};
export default FormModal;
